import { FunctionComponent, useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { IntlShape, FormattedMessage } from 'react-intl';
import { trackEvent } from '../../utils/analytics/analytics';
import {
  X as XIcon,
  MagnifyingGlass,
  BookmarkSimple,
  BookOpen,
  MapPin,
  CaretRight,
  ArrowCounterClockwise,
  Sun,
  Moon,
  Sparkle,
  SquaresFour,
  TestTube,
  Network,
  ChatText,
  ChartBar,
  CurrencyDollar,
  Robot,
  House,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { useAuthOptional } from '../../contexts/AuthContext';
import { useFeedbackModalOptional } from '../../contexts/FeedbackModalContext';
import { usePageContext } from 'vike-react/usePageContext';
import { Link } from '../Link';
import Avatar from '../Avatar';
import { useEffectiveUserProfilePhoto } from '../../hooks/useEffectiveUserProfilePhoto';
import { convertUrlToLanguage } from '../../utils/algolia/url-handling';
import { syncPreferencesToCookies } from '../../utils/shared/client-preferences';
import { useRenderLogger } from '../../utils/shared/performance-profiler';
import { getUserMenuSrpJobsRow } from '../../utils/routing/srp-user-menu-jobs-row';
import { readPrimaryBannerBottomPx } from '../../utils/dom/read-primary-banner-bottom-px';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { useMyJobsBadgeCount } from '../../hooks/useMyJobsBadgeCount';

interface MobileMenuModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  onCloseComplete: () => void;
  fullUrlContent: string;
  intl: IntlShape;
}

type Theme = 'light' | 'dark' | 'system';

const MobileMenuModalImpl: FunctionComponent<MobileMenuModalProps> = ({
  isOpen,
  onRequestClose,
  onCloseComplete,
  fullUrlContent,
  intl,
}) => {
  const auth = useAuthOptional();
  const feedbackModal = useFeedbackModalOptional();
  const user = auth?.user ?? null;
  const menuAvatarSrc = useEffectiveUserProfilePhoto(user);
  const isAuthenticated = auth?.isAuthenticated ?? false;
  const myJobsBadgeCount = useMyJobsBadgeCount();

  const srpJobsMenuRow = useMemo(
    () => getUserMenuSrpJobsRow(intl.locale, fullUrlContent),
    [intl.locale, fullUrlContent]
  );

  const runSignIn = () => {
    if (auth?.signIn) {
      auth.signIn();
      return;
    }
    if (typeof window === 'undefined') return;
    window.location.href = '/auth/login';
  };

  const runSignOut = () => {
    if (auth?.signOut) {
      auth.signOut();
      return;
    }
    if (typeof window !== 'undefined') {
      window.location.href = '/auth/logout';
    }
  };
  const pageContext = usePageContext();
  const [visible, setVisible] = useState(false);
  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement | null>(null);
  /** Desktop (lg+): fixed top = px under primary banner; null = use Tailwind fallback `lg:top-16`. */
  const [desktopMenuTopPx, setDesktopMenuTopPx] = useState<number | null>(null);
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  // Always portaled to body so scroll, clipping, and stacking are identical on SRP, job island, tools, resources.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    setMenuPortalTarget(document.body);
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const apply = () => {
      if (window.matchMedia('(max-width: 1023px)').matches) {
        setDesktopMenuTopPx(null);
        return;
      }
      setDesktopMenuTopPx(readPrimaryBannerBottomPx());
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [isOpen, visible]);
  
  const hasBackofficeAccess = isAuthenticated && (auth?.hasBackofficeAccess ?? false);
  
  // Get initial state that matches server render
  const getInitialState = () => {
    if (typeof window === 'undefined') {
      // SSR: Use server preferences from pageContext (what server actually rendered)
      const serverPrefs = (pageContext as any)?.clientPreferences;
      const state = {
        theme: (serverPrefs?.theme || 'system') as Theme,
      };
      return state;
    }
    // Client hydration: Prioritize window.__CLIENT_PREFERENCES__ to match server render
    // This is set before React hydrates by the script in +Head.tsx
    const serverClientPreferences = (window as any).__CLIENT_PREFERENCES__;
    if (serverClientPreferences) {
      // Use server preferences first to match SSR - this prevents hydration mismatch
      const state = {
        theme: (serverClientPreferences.theme || 'system') as Theme,
      };
      return state;
    }
    // Fallback to localStorage only if server preferences not available
    const savedTheme = localStorage.getItem('theme') as Theme;
    const state = {
      theme: (savedTheme || 'system') as Theme,
    };
    return state;
  };
  
  const initialState = getInitialState();
  const [theme, setTheme] = useState<Theme>(initialState.theme);
  const [isHydrated, setIsHydrated] = useState(false);
  
  // Get effective theme for scrollbar styling (Firefox) - must be after theme declaration
  const effectiveTheme = useMemo<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme === 'dark' ? 'dark' : 'light';
  }, [theme]);
  
  // Enhanced render logging to debug re-renders (only in dev - hook handles this internally)
  useRenderLogger('MobileMenuModal', {
    isOpen,
    theme,
    isHydrated,
  });
  
  // PWA install prompt
  interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  }
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  
  // Sync from localStorage after hydration (but keep initial state matching server)
  useEffect(() => {
    setIsHydrated(true);
    if (typeof window !== 'undefined') {
      // After hydration, optionally sync from localStorage if user has different preferences
      // This allows user changes to persist, but initial render matches server
      const savedTheme = localStorage.getItem('theme') as Theme;
      const serverPrefs = (window as any).__CLIENT_PREFERENCES__;
      
      // Only update if localStorage differs from server preferences
      // This prevents unnecessary re-renders if they match
      if (savedTheme && savedTheme !== serverPrefs?.theme && savedTheme !== theme) {
        setTheme(savedTheme);
      }
      
      syncPreferencesToCookies();
    }
  }, []);
  
  useEffect(() => {
    if (!isHydrated) return;
    const root = window.document.documentElement;
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    if (theme === 'system') {
      root.classList.remove('light', 'dark');
      if (systemTheme === 'dark') {
        root.classList.add('dark');
      }
      // Update theme-effective cookie so server knows the resolved theme for SSR
      const expires = new Date(Date.now() + 365*24*60*60*1000).toUTCString();
      document.cookie = `theme-effective=${encodeURIComponent(systemTheme)}; path=/; expires=${expires}; SameSite=Lax`;
    } else {
      root.classList.remove('light', 'dark');
      if (theme === 'dark') {
        root.classList.add('dark');
      }
      // Remove theme-effective cookie when not using system theme
      document.cookie = `theme-effective=; path=/; Max-Age=0; SameSite=Lax`;
    }
    localStorage.setItem('theme', theme);
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        root.classList.remove('light', 'dark');
        const newSystemTheme = e.matches ? 'dark' : 'light';
        if (e.matches) {
          root.classList.add('dark');
        }
        // Update theme-effective cookie when system theme changes
        const expires = new Date(Date.now() + 365*24*60*60*1000).toUTCString();
        document.cookie = `theme-effective=${encodeURIComponent(newSystemTheme)}; path=/; expires=${expires}; SameSite=Lax`;
      }
    };
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [theme, isHydrated]);

  
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPromptEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  
  const handlePwaInstall = () => {
    trackEvent({ name: 'pwa_install_prompt_click', params: { locale: intl.locale } });
    if (installPromptEvent) {
      installPromptEvent.prompt();
      installPromptEvent.userChoice.then(() => {
        setInstallPromptEvent(null);
      });
    }
  };

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    syncPreferencesToCookies();
    trackEvent({ name: 'theme_change', params: { theme: newTheme, locale: intl.locale } });
  };

  const handleLogin = async () => {
    trackEvent({
      name: 'auth_interaction',
      params: { action: 'sign_in_click', surface: 'mobile_menu', locale: intl.locale },
    });
    runSignIn();
    onRequestClose();
  };

  const handleLogout = () => {
    trackEvent({
      name: 'auth_interaction',
      params: { action: 'sign_out_click', surface: 'mobile_menu', locale: intl.locale },
    });
    runSignOut();
    onRequestClose();
  };

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      document.body.style.overflowX = 'hidden';
    } else {
      setVisible(false);
      closeTimeout.current = setTimeout(() => {
        onCloseComplete();
        document.body.style.overflow = '';
        document.body.style.overflowX = '';
      }, 300);
    }
    return () => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
      document.body.style.overflow = '';
      document.body.style.overflowX = '';
    };
  }, [isOpen, onCloseComplete]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onRequestClose();
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onRequestClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onRequestClose]);

  // List row component for menu items
  const MenuRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    href?: string;
    onClick?: () => void;
    iconColor?: string;
    iconBgColor?: string;
    iconColorDark?: string;
    iconBgColorDark?: string;
  }> = ({ 
    icon, 
    label, 
    href, 
    onClick, 
    iconColor = 'text-[#3B5E45]',
    iconBgColor = 'bg-[#C8DDCE]',
    iconColorDark = 'dark:text-[#98CFA8]',
    iconBgColorDark = 'dark:bg-[#1A2E22]'
  }) => {
    const handleClick = () => {
      if (onClick) {
        onClick();
      }
      onRequestClose();
    };

    const content = (
      <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-lg border border-transparent hover:bg-[#EDE3D4]/80 dark:hover:bg-[#342415]/80 hover:border-[#DDD5C8]/70 dark:hover:border-[#4A3520]/50 transition-colors overflow-hidden">
        <div className={`${iconBgColor} ${iconBgColorDark} ${iconColor} ${iconColorDark} p-1.5 rounded-full flex-shrink-0`}>
          {icon}
        </div>
        <span className="flex-1 text-sm font-medium text-[#1E1A14] dark:text-[#F2EADF]">{label}</span>
        <CaretRight className="w-4 h-4 text-[#7A6E5F] dark:text-[#8A7060] flex-shrink-0" />
      </div>
    );

    if (href) {
      return (
        <Link
          href={href}
          locale={normalizeSupportedLocale(intl.locale)}
          onClick={handleClick}
          className="cursor-pointer rounded-lg overflow-hidden block"
        >
          {content}
        </Link>
      );
    }

    return (
      <div onClick={handleClick} className="cursor-pointer rounded-lg overflow-hidden">
        {content}
      </div>
    );
  };

  // Segmented control for Language
  const LanguageSegmentedControl: React.FC = () => {
    const languages = [
      { code: 'fi', label: 'FI', fullName: 'Suomi' },
      { code: 'sv', label: 'SV', fullName: 'Svenska' },
      { code: 'en', label: 'EN', fullName: 'English' },
    ];

    const foundIndex = languages.findIndex(l => l.code === intl.locale);
    const activeIndex = foundIndex >= 0 ? foundIndex : 0; // Fallback to first language if not found
    
    // Calculate transform for indicator position (pure CSS, no DOM measurements)
    // For 3 items: 0% = first, 100% = second, 200% = third
    const translateX = `${activeIndex * 100}%`;

    return (
      <div className="relative grid grid-cols-3 items-center bg-[#F2EADF]/60 dark:bg-[#2A1D0F]/60 p-1 rounded-lg w-full gap-1 border border-[#DDD5C8]/50 dark:border-[#4A3520]/40">
        {/* Sliding background indicator - positioned using CSS transform */}
        <div
          className="absolute bg-white/95 dark:bg-[#342415]/95 rounded-md shadow dark:shadow-none border border-[#DDD5C8]/40 dark:border-[#4A3520]/40 transition-transform duration-300 ease-out top-1 h-[calc(100%-8px)] w-[calc((100%-8px-2px)/3)]"
          style={{
            transform: `translateX(${translateX})`,
            left: '4px',
          }}
        />
        {languages.map((lang, index) => {
          const isActive = intl.locale === lang.code;
          return (
            <button
              key={lang.code}
              onClick={() => {
                trackEvent({
                  name: 'locale_change',
                  params: { to_locale: lang.code, surface: 'mobile_menu' },
                });
                window.location.href = convertUrlToLanguage(fullUrlContent, lang.code, intl);
              }}
              className={`relative z-10 flex items-center justify-center gap-1.5 px-3 py-1.5 min-h-11 rounded-md transition-colors transition-transform duration-300 cursor-pointer min-w-0 ${
                isActive
                  ? 'text-[#1E1A14] dark:text-[#F2EADF] font-semibold'
                  : 'text-[#5A4A3A] dark:text-[#B8A48C] hover:text-[#1E1A14] dark:hover:text-[#F2EADF]'
              }`}
            >
              <span className="text-xs font-medium whitespace-nowrap">{lang.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  // Segmented control for Theme
  const ThemeSegmentedControl: React.FC = () => {
    const themes: { value: Theme; icon: React.ReactNode; translationKey: string; defaultLabel: string }[] = [
      { value: 'light', icon: <Sun className="w-3.5 h-3.5" />, translationKey: 'usermenu.theme.light', defaultLabel: 'Light' },
      { value: 'dark', icon: <Moon className="w-3.5 h-3.5" />, translationKey: 'usermenu.theme.dark', defaultLabel: 'Dark' },
      { value: 'system', icon: <Sparkle className="w-3.5 h-3.5" />, translationKey: 'usermenu.theme.auto', defaultLabel: 'Auto' },
    ];

    const foundIndex = themes.findIndex(t => t.value === theme);
    const activeIndex = foundIndex >= 0 ? foundIndex : 0; // Fallback to first theme if not found

    // Same positioning as LanguageSegmentedControl: 0% = first, 100% = second, 200% = third
    const translateX = `${activeIndex * 100}%`;

    return (
      <div className="relative grid grid-cols-3 items-center bg-[#F2EADF]/60 dark:bg-[#2A1D0F]/60 p-1 rounded-lg w-full gap-1 border border-[#DDD5C8]/50 dark:border-[#4A3520]/40">
        {/* Sliding background indicator - same layout as LanguageSegmentedControl */}
        <div
          className="absolute bg-white/95 dark:bg-[#342415]/95 rounded-md shadow dark:shadow-none border border-[#DDD5C8]/40 dark:border-[#4A3520]/40 transition-transform duration-300 ease-out top-1 h-[calc(100%-8px)] w-[calc((100%-8px-2px)/3)]"
          style={{
            transform: `translateX(${translateX})`,
            left: '4px',
          }}
        />
        {themes.map((themeOption, index) => {
          const isActive = theme === themeOption.value;
          return (
            <button
              key={themeOption.value}
              onClick={() => handleThemeChange(themeOption.value)}
              className={`relative z-10 flex items-center justify-center gap-1.5 px-3 py-1.5 min-h-11 rounded-md transition-colors transition-transform duration-300 cursor-pointer min-w-0 ${
                isActive
                  ? 'text-[#1E1A14] dark:text-[#F2EADF] font-semibold'
                  : 'text-[#5A4A3A] dark:text-[#B8A48C] hover:text-[#1E1A14] dark:hover:text-[#F2EADF]'
              }`}
            >
              <div 
                className={`transition-transform duration-300 flex-shrink-0 ${
                  isActive 
                    ? 'scale-110' 
                    : 'scale-100'
                }`}
              >
                {themeOption.icon}
              </div>
              <span className="text-xs font-medium whitespace-nowrap">
                {intl.formatMessage({
                  id: themeOption.translationKey,
                  defaultMessage: themeOption.defaultLabel,
                })}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const menuUi = (
    <>
      {/* Backdrop overlay - only show on md and smaller screens */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9998] bg-black/20 dark:bg-black/40 cursor-pointer lg:hidden"
          onClick={onRequestClose}
          aria-hidden="true"
        />
      )}
      {/* Menu container - responsive positioning */}
      {/* Mobile/Tablet (md and smaller): slides from bottom, Desktop (lg and larger): positioned below navbar, shrinks content width */}
      <div
        className={`fixed z-[9999] text-[#1E1A14] dark:text-[#F2EADF] transition-opacity transition-transform duration-300 ease-out ${
          isOpen && visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        } bottom-0 left-0 right-0 flex items-end justify-center px-4 lg:left-auto lg:w-[420px] lg:p-0 lg:items-stretch lg:justify-end lg:top-16 ${
          isOpen && visible
            ? 'translate-y-0 lg:translate-y-0 lg:translate-x-0'
            : 'translate-y-full lg:translate-y-0 lg:translate-x-full'
        }`}
        style={
          desktopMenuTopPx != null
            ? ({ top: desktopMenuTopPx } as React.CSSProperties)
            : undefined
        }
        onClick={(e) => e.stopPropagation()}
        aria-modal="true"
        role="dialog"
        aria-labelledby="mobile-menu-dialog-heading"
      >
        <div
          className={`mobile-menu-sheet bg-white/95 dark:bg-[#1F1509]/95 border border-[#DDD5C8]/60 dark:border-[#4A3520]/50 shadow-lg dark:shadow-[0_-4px_24px_rgba(0,0,0,0.35)] lg:dark:shadow-[4px_0_24px_rgba(0,0,0,0.35)] w-full max-w-sm lg:h-full lg:max-h-none ${
            'rounded-t-[32px] lg:rounded-none lg:max-w-none lg:border-r-0'
          }`}
        >
        {/* Header - Fixed at top */}
        <div className="relative flex shrink-0 items-center justify-center px-5 pt-5 pb-3">
          <h3 id="mobile-menu-dialog-heading" className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
            <FormattedMessage id="navigation.menu.heading" defaultMessage="Menu" description="Heading for navigation menu drawer" />
          </h3>
          <button
            onClick={onRequestClose}
            className="absolute right-5 w-11 h-11 rounded-full bg-[#F2EADF]/80 dark:bg-[#2A1D0F]/90 border border-[#DDD5C8]/60 dark:border-[#4A3520]/50 flex items-center justify-center hover:bg-[#EDE3D4] dark:hover:bg-[#342415] transition-colors cursor-pointer"
            aria-label={intl.formatMessage({ id: 'modal.close', defaultMessage: 'Close', description: 'Button aria-label to close modal/drawer' })}
          >
            <XIcon className="w-4 h-4 text-[#1E1A14] dark:text-[#F2EADF]" />
          </button>
        </div>

        {/* flex-1 + min-h-0: only reliable nested scroll when parent has definite height (.mobile-menu-sheet on mobile). */}
        <div
          className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-y-contain px-3 pb-3 max-lg:pb-[max(1.5rem,calc(0.75rem+env(safe-area-inset-bottom,0px)))] lg:pb-3 space-y-3 [-webkit-overflow-scrolling:touch] menu-scrollbar"
          style={{ scrollbarWidth: 'thin' } as React.CSSProperties}
        >
          {/* Section 1: SRP — hide on unfiltered home; reset when filtered; else Find Jobs */}
          {srpJobsMenuRow.action !== 'hide' && (
            <div className="bg-white/90 dark:bg-[#2A1D0F]/90 rounded-2xl p-1.5 space-y-1 border border-[#DDD5C8]/40 dark:border-[#4A3520]/30">
              {srpJobsMenuRow.action === 'reset_search' ? (
                <MenuRow
                  icon={<ArrowCounterClockwise className="w-4 h-4" />}
                  label={intl.formatMessage({
                    id: 'navigation.search.reset',
                    defaultMessage: 'Reset search',
                    description: 'User menu link to clear search and return to SRP home',
                  })}
                  href={srpJobsMenuRow.href}
                  iconColor="text-[#6E4F00]"
                  iconBgColor="bg-[#F5EDD0]"
                  iconColorDark="dark:text-[#D4A820]"
                  iconBgColorDark="dark:bg-[#2A1E08]"
                />
              ) : (
                <MenuRow
                  icon={<MagnifyingGlass className="w-4 h-4" />}
                  label={intl.formatMessage({
                    id: 'navigation.search.menu',
                    defaultMessage: 'Find Jobs',
                    description: 'Search link in navigation menu',
                  })}
                  href={srpJobsMenuRow.href}
                  iconColor="text-[#3B5E45]"
                  iconBgColor="bg-[#C8DDCE]"
                  iconColorDark="dark:text-[#98CFA8]"
                  iconBgColorDark="dark:bg-[#1A2E22]"
                />
              )}
            </div>
          )}

          {/* Section 1b: Feedback — relocated here from the SRP stats row (see SearchSidebar) to reclaim vertical space above the fold. */}
          {feedbackModal && (
            <div className="bg-white/90 dark:bg-[#2A1D0F]/90 rounded-2xl p-1.5 space-y-1 border border-[#DDD5C8]/40 dark:border-[#4A3520]/30">
              <MenuRow
                icon={<ChatText className="w-4 h-4" />}
                label={intl.formatMessage({
                  id: 'feedback.button.label',
                  defaultMessage: 'Give us feedback',
                  description: 'Button label to open feedback modal',
                })}
                onClick={() =>
                  feedbackModal.openFeedback({
                    source: 'mobile_menu',
                    metadata: { pathname: pageContext?.urlPathname },
                  })
                }
                iconColor="text-[#3B5E45]"
                iconBgColor="bg-[#C8DDCE]"
                iconColorDark="dark:text-[#98CFA8]"
                iconBgColorDark="dark:bg-[#1A2E22]"
              />
            </div>
          )}

          {/* Section 2: Settings (Segmented Controls) */}
          <div className="bg-white/90 dark:bg-[#2A1D0F]/90 rounded-2xl p-3 space-y-3 border border-[#DDD5C8]/40 dark:border-[#4A3520]/30">
            {/* Language Row */}
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-[#1E1A14] dark:text-[#F2EADF] flex-shrink-0">
                <FormattedMessage id="navigation.language.label" defaultMessage="Language" description="Label for language selector in navigation menu" />
              </span>
              <div className="flex-1 min-w-0 max-w-[80%]">
                <LanguageSegmentedControl />
              </div>
            </div>

            {/* Theme Row */}
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-[#1E1A14] dark:text-[#F2EADF] flex-shrink-0">
                <FormattedMessage id="usermenu.theme" defaultMessage="Theme" description="Label for theme selector in navigation menu (light/dark/auto)" />
              </span>
              <div className="flex-1 min-w-0 max-w-[80%]">
                <ThemeSegmentedControl />
              </div>
            </div>
          </div>

          {/* Section 3: My Jobs (Floating Island) — single persistent entry replaces the old
              5-link "My Tools" list; the hub's own tabs (My Jobs / Searches & Alerts / Profile &
              CV) live inside /tools itself. See SEEKER_HUB_PLAN.md §5.2. */}
          <div className="bg-white/90 dark:bg-[#2A1D0F]/90 rounded-2xl p-1.5 space-y-1 border border-[#DDD5C8]/40 dark:border-[#4A3520]/30">
            <MenuRow
              icon={<BookmarkSimple className="w-4 h-4" />}
              label={
                myJobsBadgeCount > 0
                  ? intl.formatMessage(
                      {
                        id: 'usermenu.myJobs.withCount',
                        defaultMessage: 'My Jobs ({count})',
                        description: 'Menu item label to navigate to the unified My Jobs hub, with a live count',
                      },
                      { count: myJobsBadgeCount > 99 ? '99+' : myJobsBadgeCount }
                    )
                  : intl.formatMessage({
                      id: 'hub.nav.myJobs',
                      defaultMessage: 'My Jobs',
                      description: 'Menu item label to navigate to the unified My Jobs hub',
                    })
              }
              href="/tools"
              iconColor="text-[#3B5E45]"
              iconBgColor="bg-[#C8DDCE]"
              iconColorDark="dark:text-[#98CFA8]"
              iconBgColorDark="dark:bg-[#1A2E22]"
            />
          </div>

          {/* Section 4: Discover (Floating Island) */}
          <div className="bg-white/90 dark:bg-[#2A1D0F]/90 rounded-2xl p-1.5 space-y-1 border border-[#DDD5C8]/40 dark:border-[#4A3520]/30">
            <MenuRow
              icon={<BookOpen className="w-4 h-4" />}
              label={intl.formatMessage({
                id: 'usermenu.blog',
                defaultMessage: 'Resources & Blog',
                description: 'Menu item label to navigate to resources/blog page'
              })}
              href={`/${intl.locale}/${intl.locale === 'fi' ? 'tyoelama' : intl.locale === 'sv' ? 'arbetsliv' : 'resources'}`}
              iconColor="text-[#3B5E45]"
              iconBgColor="bg-[#C8DDCE]"
              iconColorDark="dark:text-[#98CFA8]"
              iconBgColorDark="dark:bg-[#1A2E22]"
            />
            <MenuRow
              icon={<MapPin className="w-4 h-4" />}
              label={intl.formatMessage({
                id: 'navigation.locations.menu',
                defaultMessage: 'Locations Hub',
                description: 'Locations link in navigation menu'
              })}
              href={`/${intl.locale}/${intl.locale === 'fi' ? 'sijainnit' : intl.locale === 'sv' ? 'platser' : 'locations'}`}
              iconColor="text-red-600"
              iconBgColor="bg-red-100"
              iconColorDark="dark:text-red-400"
              iconBgColorDark="dark:bg-red-900/30"
            />
          </div>

          {/* Section 5: Backoffice (Floating Island) - Only for authorized users */}
          {hasBackofficeAccess && (
            <div className="bg-white/90 dark:bg-[#2A1D0F]/90 rounded-2xl p-1.5 space-y-1 border border-[#DDD5C8]/40 dark:border-[#4A3520]/30">
              <MenuRow
                icon={<House className="w-4 h-4" />}
                label={intl.formatMessage({
                  id: 'backoffice.hub.nav',
                  defaultMessage: 'All tools',
                  description: 'Navigation link to the backoffice hub listing all admin tools',
                })}
                href={`/${intl.locale}/backoffice`}
                iconColor="text-[#3B5E45]"
                iconBgColor="bg-[#C8DDCE]"
                iconColorDark="dark:text-[#98CFA8]"
                iconBgColorDark="dark:bg-[#1A2E22]"
              />
              <MenuRow
                icon={<SquaresFour className="w-4 h-4" />}
                label={intl.formatMessage({ id: 'backoffice.dashboard', defaultMessage: 'Dashboard', description: 'Navigation link to the admin dashboard for managing companies and crawls' })}
                href={`/${intl.locale}/backoffice/dashboard`}
                iconColor="text-[#3B5E45]"
                iconBgColor="bg-[#C8DDCE]"
                iconColorDark="dark:text-[#98CFA8]"
                iconBgColorDark="dark:bg-[#1A2E22]"
              />
              <MenuRow
                icon={<ChatText className="w-4 h-4" />}
                label="Feedback"
                href={`/${intl.locale}/backoffice/dashboard/feedback`}
                iconColor="text-[#3B5E45]"
                iconBgColor="bg-[#C8DDCE]"
                iconColorDark="dark:text-[#98CFA8]"
                iconBgColorDark="dark:bg-[#1A2E22]"
              />
              <MenuRow
                icon={<ChartBar className="w-4 h-4" />}
                label={intl.formatMessage({ id: 'backoffice.userAgents.nav', defaultMessage: 'User agents', description: 'Navigation link to user agent analysis' })}
                href={`/${intl.locale}/backoffice/dashboard/user-agents`}
                iconColor="text-[#6E4F00]"
                iconBgColor="bg-[#F5EDD0]"
                iconColorDark="dark:text-[#D4A820]"
                iconBgColorDark="dark:bg-[#2A1E08]"
              />
              <MenuRow
                icon={<CurrencyDollar className="w-4 h-4" />}
                label={intl.formatMessage({ id: 'backoffice.dataExplorer.nav', defaultMessage: 'Data explorer', description: 'Navigation link to data explorer' })}
                href={`/${intl.locale}/backoffice/salary-stats`}
                iconColor="text-[#3B5E45]"
                iconBgColor="bg-[#C8DDCE]"
                iconColorDark="dark:text-[#98CFA8]"
                iconBgColorDark="dark:bg-[#1A2E22]"
              />
              <MenuRow
                icon={<Robot className="w-4 h-4" />}
                label={intl.formatMessage({ id: 'backoffice.jobDataExplorer.nav', defaultMessage: 'Job data API', description: 'Navigation link to job data API and Claude prompt' })}
                href={`/${intl.locale}/backoffice/job-data-explorer`}
                iconColor="text-[#6E4F00]"
                iconBgColor="bg-[#F5EDD0]"
                iconColorDark="dark:text-[#D4A820]"
                iconBgColorDark="dark:bg-[#2A1E08]"
              />
              <MenuRow
                icon={<Network className="w-4 h-4" />}
                label={intl.formatMessage({ id: 'backoffice.architecture', defaultMessage: 'Architecture', description: 'Navigation link to the architecture documentation page' })}
                href={`/${intl.locale}/backoffice/architecture`}
                iconColor="text-[#6E4F00]"
                iconBgColor="bg-[#F5EDD0]"
                iconColorDark="dark:text-[#D4A820]"
                iconBgColorDark="dark:bg-[#2A1E08]"
              />
              <MenuRow
                icon={<TestTube className="w-4 h-4" />}
                label={intl.formatMessage({ id: 'backoffice.testing', defaultMessage: 'Testing', description: 'Navigation link to the testing tools page' })}
                href={`/${intl.locale}/backoffice/testing`}
                iconColor="text-[#3B5E45]"
                iconBgColor="bg-[#C8DDCE]"
                iconColorDark="dark:text-[#98CFA8]"
                iconBgColorDark="dark:bg-[#1A2E22]"
              />
              <MenuRow
                icon={<EnvelopeSimple className="w-4 h-4" />}
                label={intl.formatMessage({ id: 'backoffice.emailAlerts.nav', defaultMessage: 'Email alerts', description: 'Navigation link to the job-alert email compliance monitoring dashboard' })}
                href={`/${intl.locale}/backoffice/email-alerts`}
                iconColor="text-[#6E4F00]"
                iconBgColor="bg-[#F5EDD0]"
                iconColorDark="dark:text-[#D4A820]"
                iconBgColorDark="dark:bg-[#2A1E08]"
              />
            </div>
          )}

          {/* PWA Install Option */}
          {installPromptEvent && (
            <div className="bg-white/90 dark:bg-[#2A1D0F]/90 rounded-2xl p-1.5 border border-[#DDD5C8]/40 dark:border-[#4A3520]/30">
              <MenuRow
                icon={<Sparkle className="w-4 h-4" />}
                label={intl.formatMessage({
                  id: 'usermenu.installPwa',
                  defaultMessage: 'Install App',
                  description: 'Menu item label to install the Progressive Web App'
                })}
                onClick={handlePwaInstall}
                iconColor="text-[#3B5E45]"
                iconBgColor="bg-[#C8DDCE]"
                iconColorDark="dark:text-[#98CFA8]"
                iconBgColorDark="dark:bg-[#1A2E22]"
              />
            </div>
          )}
        </div>

        {/* Footer: account / login — shrink-0 so it stays below the scroll region */}
        <div className="shrink-0 px-3 pt-1.5 sm:pt-2 pb-2.5 sm:pb-3 max-lg:pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] lg:pb-3">
          {isAuthenticated && user ? (
            <div
              className="relative overflow-hidden rounded-lg sm:rounded-xl border border-[#C8BFB0] dark:border-[#4A3520] bg-gradient-to-b from-[#FDFCF9] via-[#FAF7F2] to-[#F2EDE4] dark:from-[#261C14] dark:via-[#1F170F] dark:to-[#1A130C] shadow-[0_1px_0_rgba(255,255,255,0.65)_inset,0_4px_14px_rgba(30,26,20,0.06)] sm:shadow-[0_1px_0_rgba(255,255,255,0.65)_inset,0_6px_20px_rgba(30,26,20,0.07)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
              role="region"
              aria-label={intl.formatMessage({
                id: 'usermenu.profileCv.region',
                defaultMessage: 'Your profile summary',
                description: 'Accessibility label for CV-style profile card in user menu',
              })}
            >
              <div
                className="h-0.5 sm:h-1 w-full bg-[#3B5E45] dark:bg-[#5A8A68]"
                aria-hidden
              />
              <div className="border-l-2 sm:border-l-[3px] border-l-[#3B5E45] dark:border-l-[#98CFA8] pl-2 pr-2 pt-1.5 pb-2 sm:pl-3 sm:pr-3 sm:pt-2.5 sm:pb-3">
                <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.14em] sm:tracking-[0.18em] text-[#6E4F00] dark:text-[#D4A820] mb-1 sm:mb-2 pl-0.5">
                  {intl.formatMessage({
                    id: 'usermenu.profileCv.caption',
                    defaultMessage: 'Curriculum vitae',
                    description: 'Small heading above profile block styled like a CV document',
                  })}
                </p>
                <Link
                  href={`/${intl.locale}/profile`}
                  onClick={() => onRequestClose()}
                  aria-label={intl.formatMessage({
                    id: 'usermenu.profile',
                    defaultMessage: 'My Profile',
                    description: 'Menu item label to navigate to user profile page',
                  })}
                  className="flex items-center gap-2 sm:gap-3 rounded-md sm:rounded-lg px-1.5 py-1.5 sm:px-2 sm:py-2 -mx-0.5 sm:-mx-1 border border-[#DDD5C8]/40 dark:border-[#4A3520]/35 bg-white/50 dark:bg-[#1F1509]/40 hover:bg-white/90 dark:hover:bg-[#2A1D0F]/70 hover:border-[#B8860B]/25 dark:hover:border-[#D4A820]/20 transition-colors cursor-pointer shadow-[0_1px_2px_rgba(30,26,20,0.04)]"
                >
                  <span className="flex-shrink-0 flex items-center justify-center rounded-md w-9 h-9 sm:w-11 sm:h-11 overflow-hidden ring-1 ring-[#1E1A14]/12 dark:ring-[#F2EADF]/12 shadow-sm bg-white dark:bg-[#2A1D0F]">
                    <span className="scale-[0.82] sm:scale-100 origin-center">
                      <Avatar
                        src={menuAvatarSrc}
                        size={44}
                        name={user.name || user.email || 'User'}
                      />
                    </span>
                  </span>
                  <div className="min-w-0 flex-1 text-left">
                    <p
                      className="font-['Cormorant_Garamond',Georgia,ui-serif,serif] text-[0.9375rem] sm:text-[1.125rem] md:text-[1.2rem] leading-tight sm:leading-snug font-semibold text-[#1E1A14] dark:text-[#F2EADF] truncate"
                      style={{ fontFeatureSettings: '"lnum" 1' }}
                    >
                      {user.name?.trim() ||
                        user.email ||
                        intl.formatMessage({
                          id: 'usermenu.account',
                          defaultMessage: 'Account',
                          description: 'Fallback label when user has no name or email in menu',
                        })}
                    </p>
                    {user.name?.trim() && user.email ? (
                      <p className="text-[10px] sm:text-[11px] leading-tight font-mono text-[#5A4A3A] dark:text-[#B8A48C] truncate mt-0.5 opacity-90">
                        {user.email}
                      </p>
                    ) : user.email && !user.name?.trim() ? (
                      <p className="text-[10px] sm:text-[11px] leading-tight font-mono text-[#5A4A3A] dark:text-[#B8A48C] truncate mt-0.5 opacity-90">
                        {user.email}
                      </p>
                    ) : null}
                  </div>
                  <CaretRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#7A6E5F] dark:text-[#8A7060] flex-shrink-0" />
                </Link>
                <div className="mt-2 pt-2 sm:mt-3 sm:pt-3 border-t border-double border-[#DDD5C8] dark:border-[#4A3520]">
                  <p className="text-[9px] sm:text-[10px] font-medium uppercase tracking-[0.12em] sm:tracking-[0.14em] text-[#5A4A3A] dark:text-[#B8A48C] mb-1 sm:mb-2 pl-0.5">
                    {intl.formatMessage({
                      id: 'usermenu.profileCv.session',
                      defaultMessage: 'Session',
                      description: 'Section label above logout in CV-style profile card',
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleLogout()}
                    className="w-full rounded-md px-2.5 py-2 sm:px-3 sm:py-2.5 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.1em] sm:tracking-[0.12em] text-red-700 dark:text-red-400 bg-[#FDF9F5] dark:bg-[#1A0F0C] hover:bg-[#F5EDD0] dark:hover:bg-[#342415] border border-[#C4A882]/45 dark:border-[#5A3520]/60 transition-colors cursor-pointer"
                  >
                    {intl.formatMessage({
                      id: 'usermenu.logout',
                      defaultMessage: 'Log Out',
                      description: 'Menu item label to log out of user account',
                    })}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="relative overflow-hidden rounded-lg sm:rounded-xl border border-[#C8BFB0] dark:border-[#4A3520] bg-gradient-to-b from-[#FDFCF9] via-[#FAF7F2] to-[#F2EDE4] dark:from-[#261C14] dark:via-[#1F170F] dark:to-[#1A130C] shadow-[0_1px_0_rgba(255,255,255,0.65)_inset,0_4px_14px_rgba(30,26,20,0.06)] sm:shadow-[0_1px_0_rgba(255,255,255,0.65)_inset,0_6px_20px_rgba(30,26,20,0.07)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
              role="region"
              aria-label={intl.formatMessage({
                id: 'usermenu.profileCv.regionGuest',
                defaultMessage: 'Preview profile — sign in to fill in your details',
                description: 'Accessibility label for guest CV-style card in user menu',
              })}
            >
              <div className="h-0.5 sm:h-1 w-full bg-[#3B5E45] dark:bg-[#5A8A68]" aria-hidden />
              <div className="border-l-2 sm:border-l-[3px] border-l-[#3B5E45] dark:border-l-[#98CFA8] pl-2 pr-2 pt-1.5 pb-2 sm:pl-3 sm:pr-3 sm:pt-2.5 sm:pb-3">
                <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.14em] sm:tracking-[0.18em] text-[#6E4F00] dark:text-[#D4A820] mb-1 sm:mb-2 pl-0.5">
                  {intl.formatMessage({
                    id: 'usermenu.profileCv.caption',
                    defaultMessage: 'Curriculum vitae',
                    description: 'Small heading above profile block styled like a CV document',
                  })}
                </p>
                <button
                  type="button"
                  onClick={() => handleLogin()}
                  className="w-full flex items-center gap-2 sm:gap-3 rounded-md sm:rounded-lg px-1.5 py-1.5 sm:px-2 sm:py-2 -mx-0.5 sm:-mx-1 text-left border border-dashed border-[#C4A882]/70 dark:border-[#6B5340]/80 bg-white/40 dark:bg-[#1F1509]/30 hover:bg-white/85 dark:hover:bg-[#2A1D0F]/65 hover:border-[#B8860B]/35 dark:hover:border-[#D4A820]/25 transition-colors cursor-pointer shadow-[0_1px_2px_rgba(30,26,20,0.04)]"
                >
                  <span className="flex-shrink-0 flex items-center justify-center rounded-md w-9 h-9 sm:w-11 sm:h-11 overflow-hidden ring-1 ring-dashed ring-[#C4A882]/55 dark:ring-[#6B5340]/70 bg-[#F8F5F0] dark:bg-[#2A1D0F]">
                    <span className="scale-[0.82] sm:scale-100 origin-center">
                      <Avatar
                        src={null}
                        size={44}
                        name={intl.formatMessage({
                          id: 'usermenu.profileCv.avatarGuestInitials',
                          defaultMessage: 'You',
                          description: 'Name used for placeholder avatar initials when logged out',
                        })}
                      />
                    </span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className="font-['Cormorant_Garamond',Georgia,ui-serif,serif] text-[0.9375rem] sm:text-[1.125rem] md:text-[1.2rem] leading-tight sm:leading-snug font-semibold text-[#5A4A3A] dark:text-[#B8A48C] italic truncate"
                      style={{ fontFeatureSettings: '"lnum" 1' }}
                    >
                      {intl.formatMessage({
                        id: 'usermenu.profileCv.placeholderName',
                        defaultMessage: 'Your name',
                        description: 'Placeholder CV name line when user is not logged in',
                      })}
                    </p>
                    <p className="text-[10px] sm:text-[11px] leading-tight font-mono text-[#5A4A3A] dark:text-[#B8A48C] truncate mt-0.5 opacity-90">
                      {intl.formatMessage({
                        id: 'usermenu.profileCv.placeholderEmail',
                        defaultMessage: 'you@example.com',
                        description: 'Placeholder email-style line to suggest signing in',
                      })}
                    </p>
                    <p className="text-[9px] sm:text-[10px] leading-snug text-[#5A4A3A] dark:text-[#B8A48C] mt-1 sm:mt-1.5 pr-0.5 sm:pr-1">
                      {intl.formatMessage({
                        id: 'usermenu.profileCv.placeholderHint',
                        defaultMessage: 'Sign in to save jobs, track applications, and keep your CV on JobCrawls.',
                        description: 'Encouraging hint under placeholder profile in user menu',
                      })}
                    </p>
                  </div>
                  <CaretRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#7A6E5F] dark:text-[#8A7060] flex-shrink-0 self-center" />
                </button>
                <div className="mt-2 pt-2 sm:mt-3 sm:pt-3 border-t border-double border-[#DDD5C8] dark:border-[#4A3520]">
                  <p className="text-[9px] sm:text-[10px] font-medium uppercase tracking-[0.12em] sm:tracking-[0.14em] text-[#5A4A3A] dark:text-[#B8A48C] mb-1 sm:mb-2 pl-0.5">
                    {intl.formatMessage({
                      id: 'usermenu.profileCv.signInSection',
                      defaultMessage: 'Get started',
                      description: 'Section label above login button in guest CV card',
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleLogin()}
                    className="w-full rounded-md px-2.5 py-2 sm:px-3 sm:py-2.5 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.1em] sm:tracking-[0.12em] text-[#3B5E45] dark:text-[#98CFA8] bg-[#C8DDCE]/50 dark:bg-[#1A2E22]/80 hover:bg-[#C8DDCE] dark:hover:bg-[#1A2E22] border border-[#3B5E45]/35 dark:border-[#98CFA8]/35 transition-colors cursor-pointer"
                  >
                    {intl.formatMessage({
                      id: 'usermenu.login',
                      defaultMessage: 'Login',
                      description: 'Menu item label to log in to user account',
                    })}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  );

  if (menuPortalTarget) {
    return createPortal(menuUi, menuPortalTarget);
  }

  return menuUi;
};

export const MobileMenuModal = MobileMenuModalImpl;
export default MobileMenuModalImpl;
